import { useParams, useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { PageLayout } from '../../PageLayout';
import { Customer, getCustomerById, getCustomerSubject } from '../../../store/Customer';
import { DetailPanelParams, FieldLabelMap } from '../../Compounds/DetailPanel/DetailPanel';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { AdminPanel } from './AdminPanel';
import { isAdmin } from '../../../store/User';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { PageCard } from '../../Compounds/CardWithTitle';
import { LinkComponent } from '../../Compounds/Linking/LinkComponent';
import { fetchLinkingGroups, getCruxLinkingGroupsSubject, LinkingGroupItem } from '../../../store/Link';
import { fetchUnlinkedAssets, getUnlinkedAssetSub, UnlinkedAsset } from '../../../store/Asset';
import { TicketMiniTable } from '../../Compounds/Ticket/TicketMiniTable';
import { fetchTicketsByCustId } from '../../../store/Ticket';
import { RunTotals } from '../../Compounds/RunTotal/RunTotals';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { TextSpan } from '../../Atoms/Text';
import { SuppliersFilter } from './SuppliersFilter';
import { CustomerReferences } from './CustomerReferences';

export const FIELD_LABEL_MAP: FieldLabelMap = {
  id: 'ID',
  name: 'Name',
  segment: 'Customer Segment',
  activeLocations: 'Active Locations',
  salesAgent: 'Sales Agent',
  assetCount: 'Asset Count',
  linkedAssetCount: 'Linked Asset Count',
  unlinkedAssetCount: 'Unlinked Asset Count',
  rpmAccounts: 'RPM Account Count',
  customerSuccessManager: 'CSM',
  commissionsExperienceAnalyst: 'CEA',
  billingAddress: 'Billing Address',
  customerSegment: 'Customer Segment',
  shippingAddress: 'Shipping Address',
  partnershipSegmentation: 'Partnership Segmentation',
  ultimateParent: 'Ultimate Parent',
  childCustomers: 'Child Customers List',
  parentCustomer: 'Parent Customer'
};

export const CustomerDetail = () => {
  const [customerData, setCustomerData] = useState<Customer>({} as Customer);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentSupplierId, setCurrentSupplierId] = useState<string>('');
  const { id } = useParams();
  const url = `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Account/${customerData?.sf_id || ''}/view`;
  const [groups, setGroups] = useState<LinkingGroupItem[][]>([]);
  const [unlinkedAssets, setUnlinkedAssets] = useState<UnlinkedAsset[]>([]);
  const hiddenLinkGroup = true;
  const navigate = useNavigate();

  const getData = async () => {
    if (id) {
      setLoading(true);
      const errorStatusCode = await getCustomerById(id);
      if (errorStatusCode) navigate(`/error/${errorStatusCode}/customer/${id}/${new Date().getTime()}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    const custSub = getCustomerSubject().subscribe((cust) => {
      setCustomerData(cust);
    });
    const linkSub = getCruxLinkingGroupsSubject().subscribe((g) => setGroups(g));
    const unlinkedSub = getUnlinkedAssetSub().subscribe((ua) => setUnlinkedAssets(ua));

    getData();

    return () => {
      if (custSub) custSub.unsubscribe();
      if (linkSub) linkSub.unsubscribe();
      if (unlinkedSub) unlinkedSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  useEffect(() => {
    if (!id || hiddenLinkGroup) return;
    fetchLinkingGroups(id, currentSupplierId);
    fetchUnlinkedAssets(id, currentSupplierId);
  }, [id, currentSupplierId]);

  const sections: DetailPanelParams[] = [
    {
      data: customerData,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      order: ['salesAgent', 'billingAddress', 'segment', 'shippingAddress', 'partnershipSegmentation']
    },
    {
      data: customerData,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      order: ['commissionsExperienceAnalyst', 'customerSuccessManager']
    },
    {
      data: customerData,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      order: ['ultimateParent', 'childCustomers', 'parentCustomer'],
      links: [
        { field: 'ultimateParent', link: `/customer/${customerData.ultimateParentId}`, external: false },
        { field: 'parentCustomer', link: `/customer/${customerData.parentCustomerId}`, external: false },
        { field: 'childCustomers', link: '/customer/', external: false }
      ]
    }
  ];

  const relationsContent =
    customerData?.assetCount != '0' || customerData?.paCount != '0' ? (
      <>
        <SuppliersFilter
          customerId={customerData?.id}
          currentSupplierId={currentSupplierId}
          setCurrentSupplierId={setCurrentSupplierId}
        />
        <RunTotals
          customerId={customerData?.id}
          suplierId={currentSupplierId || undefined}
        />
        <CustomerReferences
          customerId={customerData?.id}
          supplierId={currentSupplierId || undefined}
        />
        {!hiddenLinkGroup && (
          <PageCard
            title="Linking"
            headerContent={
              <TextSpan
                className="pl-2"
                color="grey4"
                size="sm">
                {groups.length} Groups | {unlinkedAssets.length} Unlinked
              </TextSpan>
            }>
            <LinkComponent
              groups={groups}
              initialVisibleCount={4}
              unlinkedItems={unlinkedAssets}
            />
          </PageCard>
        )}
      </>
    ) : (
      <PageCard title="Commissions">
        <div className="bg-blue-50 m-4 p-2 rounded flex items-center">
          <InfoIcon sx={{ fontSize: 16, color: '#1976d2', mb: '1px' }} />
          <TextSpan
            className="pl-2"
            size="sm">
            This customer has no Assets or Provisional Assets.
          </TextSpan>
        </div>
      </PageCard>
    );

  return (
    <PageLayout
      className="mb-10"
      dataTestId="Customer-detail-page"
      subTitle="Customer"
      pageTitle={customerData?.name}
      showLink={true}
      link={url}
      showIcon={true}
      key={customerData?.id}
      icon="customerMed">
      <Fragment>
        {loading && <ComponentSpinner sx={{ height: '35rem', margin: 'auto' }} />}
        {!loading && (
          <div className="w-full flex mt-3 space-x-6">
            <div className="w-8/12 space-y-6">{relationsContent}</div>
            <div className="w-4/12 self-start space-y-6">
              <PageCard title="Info">
                <DetailPanelContainer data={sections} />
              </PageCard>
              <TicketMiniTable
                id={customerData.id}
                fetchFunction={fetchTicketsByCustId}
                showCreate={false}
                objectType="customer"
              />
              <DetailsComments
                id={customerData.id}
                entityType="customer"
              />
              <DetailViewCard
                enableCollapse
                collapsed
                key="service_locations_table"
                title="Service Locations">
                <div className="flex justify-center p-8">Under construction</div>
              </DetailViewCard>
              {isAdmin() && <AdminPanel data={customerData} />}
            </div>
          </div>
        )}
      </Fragment>
    </PageLayout>
  );
};
